import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  Skeleton,
} from '@mui/material';
import { Add, Refresh, Edit, Delete, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { fetchExchanges, updateExchange, createExchange, deleteExchange } from '../store/exchangesSlice';
import api from '../services/api';
import { ExchangeIcon } from '../components/IconDisplay';
import { useConfirm } from '../components/ConfirmDialog';

function Exchanges() {
  const dispatch = useDispatch();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { exchanges, loading } = useSelector((state) => state.exchanges);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [editingExchange, setEditingExchange] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [apiPassphrase, setApiPassphrase] = useState('');
  const [savingApiKeys, setSavingApiKeys] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  // Snackbar state for better notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success', 'error', 'warning', 'info'
  });
  
  // State for adding new exchange
  const [newExchangeName, setNewExchangeName] = useState('');
  const [newExchangeIsDemo, setNewExchangeIsDemo] = useState(false);
  const [newExchangeMarketType, setNewExchangeMarketType] = useState('SPOT');  // Single market type selection
  const [formError, setFormError] = useState('');
  const [editExchangeName, setEditExchangeName] = useState('');
  const [editExchangeIsDemo, setEditExchangeIsDemo] = useState(false);
  const [editExchangeMarketType, setEditExchangeMarketType] = useState('SPOT');  // Single market type selection

  useEffect(() => {
    dispatch(fetchExchanges());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchExchanges());
  };

  const handleToggleActive = (exchange) => {
    // This doesn't exist in the Exchange model, removed this functionality
    // The is_active field is on TradingPair, not Exchange
  };

  const handleConfigureExchange = (exchange) => {
    setSelectedExchange(exchange);
    setApiKey('');
    setApiSecret('');
    setApiPassphrase('');
    setDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    console.log('Save button clicked!');
    if (selectedExchange) {
      // Validate that required fields are filled
      if (!apiKey || !apiSecret) {
        setSnackbar({
          open: true,
          message: 'API Key and API Secret are required fields',
          severity: 'warning'
        });
        return;
      }
      
      setSavingApiKeys(true);
      
      const data = {
        access_key: apiKey,
        secret_key: apiSecret,
      };
      
      // Only include passphrase if it's provided
      if (apiPassphrase) {
        data.passphrase = apiPassphrase;
      }
      
      try {
        console.log('Configuring API for exchange:', selectedExchange.name);
        console.log('Request data:', data);
        
        // Call the configure_api endpoint
        const response = await api.post(`/api/exchanges/${selectedExchange.id}/configure_api/`, data);
        
        console.log('API configuration response:', response.data);
        
        // Check validation result
        if (response.data.validation && response.data.validation.valid) {
          // Show success message with validation details
          setSnackbar({
            open: true,
            message: `API keys validated successfully! Found ${response.data.validation.balances_count || 0} balances.`,
            severity: 'success'
          });
        } else {
          // This shouldn't happen with current backend logic, but just in case
          setSnackbar({
            open: true,
            message: 'API keys configured but validation status unknown',
            severity: 'warning'
          });
        }
        
        // Refresh exchanges to update API status
        await dispatch(fetchExchanges());
        
        setDialogOpen(false);
      } catch (error) {
        console.error('Failed to configure API:', error);
        console.error('Error response:', error.response);
        
        // Show detailed error message to user
        let errorMessage = 'Failed to configure API keys.';
        
        if (error.response?.data?.validation) {
          const validation = error.response.data.validation;
          errorMessage = `${validation.error || 'Unknown error'}. Please check your API credentials and permissions.`;
        } else {
          errorMessage = error.response?.data?.error || error.response?.data?.detail || errorMessage;
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setSavingApiKeys(false);
      }
    }
  };

  const handleRemoveApiKeys = async (exchange) => {
    const ok = await confirm({
      title: `Remove API keys for ${exchange.name}?`,
      message: 'The exchange will stop placing and reading orders until new keys are added. Existing open orders on the exchange are not affected.',
      confirmLabel: 'Remove keys',
      severity: 'warning',
    });
    if (!ok) return;
    try {
      const response = await api.delete(`/api/exchanges/${exchange.id}/remove_api_keys/`);
      console.log('Remove API keys response:', response.data);

      setSnackbar({
        open: true,
        message: `API keys removed successfully for ${exchange.name}`,
        severity: 'success'
      });

      // Refresh exchanges to update API status
      await dispatch(fetchExchanges());
    } catch (error) {
      console.error('Failed to remove API keys:', error);
      setSnackbar({
        open: true,
        message: `Failed to remove API keys: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleTestConnection = async () => {
    if (selectedExchange) {
      setTestingConnection(true);
      try {
        const response = await api.post(`/api/exchanges/${selectedExchange.id}/test_api_keys/`);
        console.log('Test connection response:', response.data);
        
        if (response.data.validation && response.data.validation.valid) {
          setSnackbar({
            open: true,
            message: `Connection test successful! Found ${response.data.validation.balances_count || 0} balances.`,
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Connection test failed: Unknown response',
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Connection test failed:', error);
        let errorMessage = 'Connection test failed.';
        
        if (error.response?.status === 404) {
          errorMessage = 'No API keys configured for this exchange. Please save API keys first.';
        } else if (error.response?.data?.validation) {
          const validation = error.response.data.validation;
          errorMessage = `Connection test failed: ${validation.error || 'Unknown error'}`;
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setTestingConnection(false);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedExchange(null);
    setApiKey('');
    setApiSecret('');
    setApiPassphrase('');
    setSavingApiKeys(false);
    setTestingConnection(false);
  };

  const handleOpenAddDialog = () => {
    setNewExchangeName('');
    setNewExchangeMarketType('SPOT');
    setNewExchangeIsDemo(false);
    setFormError('');
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setNewExchangeName('');
    setNewExchangeIsDemo(false);
    setNewExchangeMarketType('SPOT');
    setFormError('');
  };

  const handleOpenEditDialog = (exchange) => {
    setEditingExchange(exchange);
    setEditExchangeName(exchange.name);
    setEditExchangeIsDemo(exchange.is_demo || false);
    setEditExchangeMarketType(exchange.market_type || 'SPOT');  // Load current market type

    setFormError('');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingExchange(null);
    setEditExchangeName('');
    setEditExchangeIsDemo(false);
    setEditExchangeMarketType('SPOT');
    setFormError('');
  };

  const handleEditExchange = async () => {
    if (!editExchangeName.trim()) {
      setFormError('Exchange name is required');
      return;
    }

    if (!editExchangeMarketType) {
      setFormError('Please select a market type');
      return;
    }

    try {
      // Update exchange with new structure
      await dispatch(updateExchange({
        id: editingExchange.id,
        data: {
          name: editExchangeName.trim(),
          market_type: editExchangeMarketType,
          is_demo: editExchangeIsDemo
        }
      })).unwrap();

      dispatch(fetchExchanges());
      handleCloseEditDialog();
    } catch (error) {
      setFormError(error.message || 'Failed to update exchange');
    }
  };

  const handleDeleteExchange = async (exchangeId) => {
    const ok = await confirm({
      title: 'Delete exchange?',
      message: 'This also removes any trading pairs and triggers bound to the exchange. Historical trade records remain intact.',
      confirmLabel: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await dispatch(deleteExchange(exchangeId)).unwrap();
      dispatch(fetchExchanges());
    } catch (error) {
      console.error('Failed to delete exchange:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete exchange. It may be in use by trading pairs or triggers.',
        severity: 'error'
      });
    }
  };

  const handleAddExchange = async () => {
    // Validate
    if (!newExchangeName.trim()) {
      setFormError('Exchange name is required');
      return;
    }

    if (!newExchangeMarketType) {
      setFormError('Please select a market type');
      return;
    }

    try {
      // Create the exchange with market_type (new structure)
      await dispatch(createExchange({
        name: newExchangeName.trim(),
        market_type: newExchangeMarketType,
        is_demo: newExchangeIsDemo
      })).unwrap();

      // Refresh exchanges to show the new one
      dispatch(fetchExchanges());
      handleCloseAddDialog();
    } catch (error) {
      setFormError(error.message || 'Failed to create exchange');
    }
  };

  // Check if exchange has API key configured
  const hasApiKey = (exchange) => {
    return exchange.has_api_key || false;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Exchange Configuration</Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} sx={{ mr: 1 }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
            >
              Add Exchange
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Exchange Name</TableCell>
              <TableCell>Market Type</TableCell>
              <TableCell>API Configured</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && exchanges.length === 0 ? (
              // Skeleton loaders while initial data is loading
              Array(3).fill(0).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={100} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  <TableCell><Skeleton variant="text" width={30} /></TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Skeleton variant="rounded" width={100} height={30} />
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : !Array.isArray(exchanges) ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Error: Invalid data format received from API
                </TableCell>
              </TableRow>
            ) : exchanges.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No exchanges configured
                </TableCell>
              </TableRow>
            ) : (
              exchanges.map((exchange) => (
                <TableRow key={exchange.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ExchangeIcon exchangeName={exchange.name} size={20} />
                      {exchange.display_name || exchange.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {exchange.market_type || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {hasApiKey(exchange) ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Configure API">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleConfigureExchange(exchange)}
                        >
                          Configure API
                        </Button>
                      </Tooltip>
                      {hasApiKey(exchange) && (
                        <Tooltip title="Remove API Keys">
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={() => handleRemoveApiKeys(exchange)}
                          >
                            Remove Keys
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit Exchange">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(exchange)}
                          aria-label={`Edit ${exchange.display_name || exchange.name} exchange`}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Exchange">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteExchange(exchange.id)}
                          aria-label={`Delete ${exchange.display_name || exchange.name} exchange`}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Exchange Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Exchange</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Exchange Name"
              value={newExchangeName}
              onChange={(e) => setNewExchangeName(e.target.value)}
              margin="normal"
              required
              autoFocus
              helperText="e.g., Binance, Coinbase, Kraken"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={newExchangeIsDemo}
                  onChange={(e) => setNewExchangeIsDemo(e.target.checked)}
                  name="is_demo"
                />
              }
              label="Demo/Testnet Mode (Binance only)"
            />
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4, mt: -1, mb: 2 }}>
              When enabled, uses Binance testnet endpoints. You must manually update API keys to testnet keys.
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel>Market Type</InputLabel>
              <Select
                value={newExchangeMarketType}
                onChange={(e) => setNewExchangeMarketType(e.target.value)}
                label="Market Type"
              >
                <MenuItem value="SPOT">SPOT Market</MenuItem>
                <MenuItem value="USD_M">USD-M Futures</MenuItem>
                <MenuItem value="COIN_M">COIN-M Futures</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              Each exchange can have only one market type. Create multiple exchanges for different markets.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddExchange} variant="contained">
            Add Exchange
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configure API Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configure API for {selectedExchange?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              margin="normal"
              type="password"
              required
            />
            <TextField
              fullWidth
              label="API Secret"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              margin="normal"
              type="password"
              required
            />
            <TextField
              fullWidth
              label="API Passphrase (if required)"
              value={apiPassphrase}
              onChange={(e) => setApiPassphrase(e.target.value)}
              margin="normal"
              type="password"
              helperText="Some exchanges like Coinbase require a passphrase"
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
              Note: API credentials are encrypted and stored securely.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button 
              type="button"
              onClick={handleTestConnection}
              disabled={testingConnection || savingApiKeys}
              color="info"
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </Button>
            <Box>
              <Button type="button" onClick={handleCloseDialog} disabled={savingApiKeys || testingConnection}>
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleSaveConfig} 
                variant="contained"
                disabled={savingApiKeys || testingConnection || !apiKey || !apiSecret}
                sx={{ ml: 1 }}
              >
                {savingApiKeys ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit Exchange Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Exchange</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Exchange Name"
              value={editExchangeName}
              onChange={(e) => setEditExchangeName(e.target.value)}
              margin="normal"
              required
              autoFocus
              helperText="e.g., Binance, Coinbase, Kraken"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={editExchangeIsDemo}
                  onChange={(e) => setEditExchangeIsDemo(e.target.checked)}
                  name="is_demo"
                />
              }
              label="Demo/Testnet Mode (Binance only)"
            />
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4, mt: -1, mb: 2 }}>
              When enabled, uses Binance testnet endpoints. You must manually update API keys to testnet keys.
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel>Market Type</InputLabel>
              <Select
                value={editExchangeMarketType}
                onChange={(e) => setEditExchangeMarketType(e.target.value)}
                label="Market Type"
              >
                <MenuItem value="SPOT">SPOT Market</MenuItem>
                <MenuItem value="USD_M">USD-M Futures</MenuItem>
                <MenuItem value="COIN_M">COIN-M Futures</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              Each exchange can have only one market type.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleEditExchange} variant="contained">
            Update Exchange
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          icon={snackbar.severity === 'success' ? <CheckCircle /> : snackbar.severity === 'error' ? <ErrorIcon /> : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {confirmDialog}
    </Box>
  );
}

export default Exchanges;